/* <fb-shoot>: the Record Shop Shoot (sections/fb-shoot.liquid).
   - "See all" shows the frames held back after the first ones.
   - The darkroom: a print develops once a third of it is in view and its photo has
     arrived (the section's inline script decides whether there is a darkroom at all).
   - The lightbox: a native <dialog>; arrows, keys, swipe and the film strip move it; a
     click on the photo zooms in on the print and the pointer pans; Esc or the cross
     closes it and gives focus back to the tile that opened it.
   - No save: the context menu and dragging are cancelled on every photo
     ([data-fbs-nosave]); the product cards keep theirs. */
(function () {
  if (!window.customElements || customElements.get('fb-shoot')) return;

  var SWIPE = 50; // px across before a swipe changes frame
  var MOVE = 6; // px between press and release before it is a drag, not a click
  var STAGGER = 110; // ms between prints that come into view together

  function still() {
    return window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class FBShoot extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      this.tiles = Array.prototype.slice.call(this.querySelectorAll('[data-fbs-tile]'));
      this.box = this.querySelector('[data-fbs-box]');
      if (!this.box || !this.tiles.length) return;
      this.stage = this.box.querySelector('[data-fbs-stage]');
      this.img = this.box.querySelector('[data-fbs-img]');
      this.strip = this.box.querySelector('[data-fbs-strip]');
      this.cards = this.box.querySelector('[data-fbs-cards]');
      this.live = this.box.querySelector('[data-fbs-live]');
      this.zoomBtn = this.box.querySelector('[data-fbs-zoom]');
      this.index = 0;
      this.zoomed = false;

      this.wireSeeAll();
      this.develop();
      this.wireNoSave();
      this.wireBox();

      this.addEventListener('click', (e) => {
        var open = e.target.closest('[data-fbs-open]');
        if (!open || !this.contains(open)) return;
        this.open(this.tiles.indexOf(open.closest('[data-fbs-tile]')), open);
      });
    }

    wireSeeAll() {
      var more = this.querySelector('[data-fbs-see-all]');
      if (!more) return;
      if (this.querySelector('[data-fbs-more]')) more.hidden = false;
      more.addEventListener('click', () => {
        this.classList.add('is-all');
        var first = this.querySelector('[data-fbs-more] [data-fbs-open]');
        if (first) first.focus({ preventScroll: true });
      });
    }

    develop() {
      if (!this.classList.contains('is-darkroom')) return;
      var io = new IntersectionObserver(
        (entries) => {
          var k = 0;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            io.unobserve(entry.target);
            var tile = entry.target;
            var delay = Math.min(k++, 6) * STAGGER;
            var img = tile.querySelector('.fb-shoot__img');
            var go = function () {
              tile.style.setProperty('--fb-delay', delay + 'ms');
              tile.classList.add('is-developed');
              // Done: drop the filter altogether rather than keep 48 identity filters alive.
              if (img) img.addEventListener('animationend', () => tile.classList.add('is-printed'), { once: true });
            };
            if (!img || (img.complete && img.naturalWidth)) return go();
            img.addEventListener('load', go, { once: true });
            img.addEventListener('error', go, { once: true });
          });
        },
        { threshold: 0.35 }
      );
      this.tiles.forEach((t) => io.observe(t));
    }

    wireNoSave() {
      var guard = function (e) {
        if (e.target.closest && e.target.closest('[data-fbs-nosave]')) e.preventDefault();
      };
      this.addEventListener('contextmenu', guard);
      this.addEventListener('dragstart', guard);
    }

    wireBox() {
      var box = this.box;
      box.querySelector('[data-fbs-close]').addEventListener('click', () => box.close());
      box.querySelector('[data-fbs-prev]').addEventListener('click', () => this.go(-1));
      box.querySelector('[data-fbs-next]').addEventListener('click', () => this.go(1));
      this.zoomBtn.addEventListener('click', () => this.zoom(!this.zoomed));
      box.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.go(1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.go(-1);
        }
      });
      box.addEventListener('close', () => this.closed());
      this.strip.addEventListener('click', (e) => {
        var b = e.target.closest('button[data-i]');
        if (b) this.show(Number(b.dataset.i));
      });

      // One gesture, three meanings: a swipe changes frame, a drag on a zoomed print pans,
      // a click zooms in or out. The click that follows a swipe or a drag is swallowed.
      var down = null;
      var moved = false;
      this.stage.addEventListener('pointerdown', (e) => {
        down = { x: e.clientX, y: e.clientY };
        moved = false;
        if (this.stage.setPointerCapture) this.stage.setPointerCapture(e.pointerId);
      });
      this.stage.addEventListener('pointermove', (e) => {
        if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > MOVE) moved = true;
        if (this.zoomed && (e.pointerType === 'mouse' || down)) {
          this.stage.classList.add('is-panning');
          this.aim(e.clientX, e.clientY);
        }
      });
      this.stage.addEventListener('pointerup', (e) => {
        if (!down) return;
        var dx = e.clientX - down.x;
        var dy = e.clientY - down.y;
        down = null;
        if (!this.zoomed && Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this.swallow = true;
          this.go(dx < 0 ? 1 : -1);
        } else if (moved) {
          this.swallow = true;
        }
      });
      this.stage.addEventListener('pointercancel', () => {
        down = null;
      });
      this.stage.addEventListener('click', (e) => {
        if (this.swallow) {
          this.swallow = false;
          return;
        }
        this.zoom(!this.zoomed, e.clientX, e.clientY);
      });
    }

    open(i, opener) {
      if (i < 0) return;
      this.opener = opener || null;
      if (!this.strip.childElementCount) this.buildStrip();
      document.documentElement.classList.add('fb-shoot-lock');
      this.box.showModal();
      this.show(i, true);
    }

    closed() {
      this.zoom(false);
      document.documentElement.classList.remove('fb-shoot-lock');
      if (this.opener && this.opener.isConnected) this.opener.focus();
    }

    buildStrip() {
      var frag = document.createDocumentFragment();
      this.tiles.forEach((t, i) => {
        var b = document.createElement('button');
        b.type = 'button';
        b.dataset.i = String(i);
        b.setAttribute('aria-label', t.dataset.fbsLabel || '');
        var im = document.createElement('img');
        im.src = t.dataset.fbsThumb;
        im.alt = '';
        im.loading = 'lazy';
        im.decoding = 'async';
        im.draggable = false;
        b.appendChild(im);
        frag.appendChild(b);
      });
      this.strip.appendChild(frag);
    }

    sizes() {
      return '(min-width: 990px) calc(100vw - 44rem), 100vw';
    }

    go(step) {
      this.show(this.index + step);
    }

    show(i, first) {
      var n = this.tiles.length;
      i = ((i % n) + n) % n;
      this.index = i;
      this.zoom(false);
      var t = this.tiles[i];
      var tileImg = t.querySelector('.fb-shoot__img');
      this.img.sizes = this.sizes();
      this.img.srcset = t.dataset.fbsSrcset;
      this.img.alt = tileImg ? tileImg.alt : '';

      var num = String(i + 1);
      this.box.querySelector('[data-fbs-n]').textContent = num.length < 2 ? '0' + num : num;
      this.box.querySelector('[data-fbs-scene]').textContent = t.dataset.fbsScene || '';
      var look = t.querySelector('template[data-fbs-look]');
      this.cards.replaceChildren(look ? look.content.cloneNode(true) : '');
      this.live.textContent = (this.box.dataset.fbsLiveText || '')
        .replace('__N__', i + 1)
        .replace('__T__', n)
        .replace('__S__', t.dataset.fbsScene || '');

      var was = this.strip.querySelector('[aria-current]');
      if (was) was.removeAttribute('aria-current');
      var b = this.strip.children[i];
      if (b) {
        b.setAttribute('aria-current', 'true');
        this.strip.scrollTo({
          left: b.offsetLeft - (this.strip.clientWidth - b.offsetWidth) / 2,
          behavior: first || still() ? 'auto' : 'smooth',
        });
      }

      // The neighbours load now, so the next arrow is instant.
      [i - 1, i + 1].forEach((j) => {
        var nb = this.tiles[((j % n) + n) % n];
        var pre = new Image();
        pre.sizes = this.sizes();
        pre.srcset = nb.dataset.fbsSrcset;
      });
    }

    zoom(on, x, y) {
      this.zoomed = !!on;
      this.stage.classList.remove('is-panning');
      this.stage.classList.toggle('is-zoomed', this.zoomed);
      this.zoomBtn.setAttribute('aria-pressed', String(this.zoomed));
      this.zoomBtn.setAttribute('aria-label', this.zoomed ? this.box.dataset.fbsZoomOut : this.box.dataset.fbsZoomIn);
      if (this.zoomed) {
        this.aim(x, y);
        // The largest source: the browser swaps it in when it has arrived.
        this.img.sizes = '300vw';
      } else {
        this.img.style.removeProperty('--ox');
        this.img.style.removeProperty('--oy');
      }
    }

    // Magnify around the pointer: the point under it stays under it.
    aim(x, y) {
      var r = this.stage.getBoundingClientRect();
      var ox = x == null ? 50 : ((x - r.left) / r.width) * 100;
      var oy = y == null ? 50 : ((y - r.top) / r.height) * 100;
      this.img.style.setProperty('--ox', Math.max(0, Math.min(100, ox)) + '%');
      this.img.style.setProperty('--oy', Math.max(0, Math.min(100, oy)) + '%');
    }
  }

  customElements.define('fb-shoot', FBShoot);
})();
